// Admin / Auswertungs-View — what the survey creator sees at /admin/<token>.

import { ApiError, getAdminSurvey, updateSurvey } from '../api';
import type {
  AdminSurveyResponse,
  Message,
  SurveyVote,
  VoterVisibility,
} from '../api';
import type { Appointment } from '../types';
import { sortAppointments } from '../logic';
import { formatDateShort, formatTime } from '../time';
import { renderAdminMatrix } from './admin-matrix';
import { renderAppHeader } from './app-header';
import { renderResultCard } from './result-card';
import { renderAdminVoteTable } from './admin-vote-table';
import { buildVoterShareMessage } from '../share-message';
import {
  loadImportance, saveImportance, weightFor, type ImportanceMap,
} from '../voter-importance';
import { el, clear } from './dom';

type ViewState = {
  data: AdminSurveyResponse;
  /** Index in data.votes that is currently spotlit, or null = aggregate view. */
  spotlightIndex: number | null;
  /** Wichtigkeits-Sterne pro Voter-Pseudonym, lokal im Browser. */
  importance: ImportanceMap;
  /** Best-Slots-View-Toggle: Tabelle vs Tier-Karten. */
  bestSlotsView: 'table' | 'cards';
};

export function renderAdminView(root: HTMLElement, token: string): void {
  const container = el('div', { class: 'admin-view' });
  root.append(renderAppHeader(), container);

  const reload = () => {
    showLoading(container);
    getAdminSurvey(token)
      .then((data) => {
        const state: ViewState = {
          data,
          spotlightIndex: null,
          importance: loadImportance(data.id),
          bestSlotsView: 'table',
        };
        showLoaded(container, state, reload);
      })
      .catch((e) => showError(container, e));
  };

  reload();
}

function showLoading(container: HTMLElement): void {
  clear(container);
  container.append(el('div', { class: 'view-loading' }, 'Lade Umfrage …'));
}

function showError(container: HTMLElement, e: unknown): void {
  clear(container);
  let title = 'Umfrage konnte nicht geladen werden';
  let body = 'Bitte prüfe den Link.';
  if (e instanceof ApiError) {
    if (e.status === 404) {
      title = 'Umfrage nicht gefunden';
      body = 'Der Admin-Link ist ungültig oder die Umfrage wurde gelöscht.';
    } else if (e.status === 410) {
      title = 'Umfrage ist abgelaufen';
      body = 'Diese Umfrage wurde nach Ablauf automatisch gelöscht.';
    } else {
      body = `Server: ${e.message} (Status ${e.status}).`;
    }
  } else if (e instanceof Error) {
    body = e.message;
  }
  container.append(
    el('div', { class: 'view-error' },
      el('h2', { class: 'view-error-title' }, title),
      el('p', { class: 'view-error-body' }, body),
      el('a', { class: 'view-error-link', href: '/' }, '← Zurück zur Startseite'),
    ),
  );
}

function showLoaded(
  container: HTMLElement,
  state: ViewState,
  reload: () => void,
): void {
  clear(container);
  const { data } = state;
  const title = data.title?.trim() || 'Slot Umfrage';
  const voterUrl = `${window.location.origin}/vote/${data.voter_token}`;

  container.append(renderHeader(title, data, voterUrl, reload));

  // Result-Card sitzt ganz oben, wenn die Survey finalisiert ist.
  const resultSection = el('div', { class: 'admin-result-host' });
  // Finalisieren-Sektion erscheint statt Result-Card, wenn noch offen.
  const finalizeSection = el('div', { class: 'admin-finalize-host' });
  const matrixSection = el('div', { class: 'admin-matrix-host' });
  const voteTableSection = el('div', { class: 'admin-vote-table-host' });
  const bestSection = el('div', { class: 'admin-best-host' });
  const slotsSection = el('div', { class: 'admin-slots-host' });
  const votersSection = el('div', { class: 'admin-voters-host' });
  const messagesSection = el('div', { class: 'admin-messages-host' });

  const setSpotlight = (idx: number | null) => {
    state.spotlightIndex = idx;
    rerender();
    matrixSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const rerender = () => {
    const spotlightVote = state.spotlightIndex !== null
      ? data.votes[state.spotlightIndex] ?? null
      : null;
    const activePseudonym = spotlightVote?.pseudonym ?? null;

    // Result-Card oben, wenn finalisiert. Sonst Finalize-Sektion.
    clear(resultSection);
    clear(finalizeSection);
    const isFinalized = data.final_slot_ids !== null && data.final_slot_ids.length > 0;
    if (isFinalized) {
      const finalSlots = data.slots.filter((s) => data.final_slot_ids?.includes(s.id));
      resultSection.append(renderResultCard({
        surveyId: data.id,
        title: data.title,
        voterToken: data.voter_token,
        finalSlots,
        finalMessage: data.final_message,
        location: data.location,
        audience: 'admin',
        onUndo: async () => {
          try {
            await updateSurvey(data.admin_token, { final_slot_ids: [], final_message: null });
            reload();
          } catch (e) {
            window.alert('Aufheben fehlgeschlagen: ' + (e instanceof Error ? e.message : 'unbekannt'));
          }
        },
      }));
    } else {
      finalizeSection.append(renderFinalizeSection(data, reload));
    }

    clear(matrixSection);
    if (spotlightVote) {
      matrixSection.append(renderSpotlightBanner(spotlightVote, () => setSpotlight(null)));
    }
    matrixSection.append(renderAdminMatrix(data.config, data.slots, data.votes, spotlightVote));

    clear(voteTableSection);
    voteTableSection.append(renderAdminVoteTable({
      surveyId: data.id,
      slots: data.slots,
      votes: data.votes,
      importance: state.importance,
      onImportanceChange: rerender,
      activePseudonym,
      setSpotlight,
    }));

    clear(bestSection);
    bestSection.append(renderBestSlotsSection(state, activePseudonym, setSpotlight, rerender));

    clear(slotsSection);
    slotsSection.append(renderSlotsSection(data.slots, data.votes, activePseudonym, setSpotlight));

    clear(votersSection);
    votersSection.append(renderVotersSection(data, state, () => setSpotlight(state.spotlightIndex)));

    clear(messagesSection);
    messagesSection.append(renderMessagesSection(data.messages));
  };

  container.append(resultSection, finalizeSection, voteTableSection, matrixSection, bestSection, slotsSection, votersSection, messagesSection);
  rerender();
}

/** Sektion zum Auswählen + Fixieren der finalen Slots. Sichtbar nur wenn
 *  noch nicht finalisiert. Multi-Select per Checkbox neben jedem Slot. */
function renderFinalizeSection(data: AdminSurveyResponse, reload: () => void): HTMLElement {
  const section = el('section', { class: 'admin-section admin-finalize' });
  section.append(
    el('h2', { class: 'admin-section-heading' }, '🏁 Termin fixieren'),
    el('p', { class: 'admin-finalize-hint' },
      'Wenn du dich entschieden hast, wähle einen oder mehrere Slots aus und schließe die Umfrage. ',
      'Voter sehen dann oben eine Ergebnis-Karte, die sie sich in den Kalender legen oder teilen können.',
    ),
  );

  if (data.slots.length === 0) {
    section.append(el('p', { class: 'admin-empty' }, 'Keine Slots zum Fixieren.'));
    return section;
  }

  const sortedSlots = [...data.slots].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    return a.startMinutes - b.startMinutes;
  });
  const selected = new Set<string>();

  // Empfehlung: Slot mit dem höchsten Score (yes*2 + maybe).
  // Bei Gleichstand zählt die ✓-Anzahl, dann der frühere Termin.
  // Nur empfehlen, wenn überhaupt schon mind. eine Stimme da ist.
  const recommendedId = data.votes.length > 0
    ? recommendBestSlot(sortedSlots, data.votes)
    : null;
  if (recommendedId !== null) {
    selected.add(recommendedId);
  }

  const list = el('ul', { class: 'admin-finalize-list' });
  for (const s of sortedSlots) {
    const item = el('li', { class: 'admin-finalize-item' });
    if (s.id === recommendedId) item.classList.add('is-recommended');
    const cb = el('input', { type: 'checkbox', class: 'admin-finalize-cb', id: `fin-${s.id}` }) as HTMLInputElement;
    if (s.id === recommendedId) cb.checked = true;
    cb.addEventListener('change', () => {
      if (cb.checked) selected.add(s.id);
      else selected.delete(s.id);
      updateButtons();
    });
    const stats = computeSlotStats(s.id, data.votes);
    const label = el('label', { for: `fin-${s.id}`, class: 'admin-finalize-label' });
    label.append(
      cb,
      el('span', { class: 'admin-finalize-when' },
        el('strong', {}, formatDateShort(s.date)),
        ` · ${formatTime(s.startMinutes)}–${formatTime(s.endMinutes)}`,
      ),
      el('span', { class: 'admin-finalize-stats' },
        `${stats.yes} ✓ · ${stats.maybe} ? · ${stats.no} ✗`,
      ),
    );
    if (s.id === recommendedId) {
      label.append(el('span',
        { class: 'admin-finalize-badge', title: 'Bester Score: meiste ✓-Stimmen, dann ?-Stimmen, dann frühestes Datum' },
        '⭐ Empfohlen',
      ));
    }
    item.append(label);
    list.append(item);
  }
  section.append(list);

  const messageWrap = el('div', { class: 'admin-finalize-message-wrap' });
  const messageLabel = el('label',
    { class: 'admin-finalize-message-label', for: 'admin-finalize-message' },
    'Optionale Nachricht für die Voter (z. B. „Wir treffen uns im Café Adler"):',
  );
  const messageInput = el('textarea', {
    id: 'admin-finalize-message',
    class: 'admin-finalize-message',
    rows: '2',
    maxlength: 500,
    placeholder: 'Optional — wird mit dem Ergebnis angezeigt.',
  }) as HTMLTextAreaElement;
  messageWrap.append(messageLabel, messageInput);
  section.append(messageWrap);

  const actions = el('div', { class: 'admin-finalize-actions' });
  const finalizeBtn = el('button',
    { type: 'button', class: 'btn-primary admin-finalize-go', disabled: '' },
    '🏁 Auswahl fixieren',
  ) as HTMLButtonElement;
  const hint = el('span', { class: 'admin-finalize-action-hint' }, 'Wähle mind. 1 Slot');
  actions.append(finalizeBtn, hint);
  section.append(actions);

  function updateButtons(): void {
    const n = selected.size;
    finalizeBtn.disabled = n === 0;
    finalizeBtn.textContent = n <= 1
      ? '🏁 Auswahl fixieren'
      : `🏁 ${n} Slots fixieren`;
    hint.textContent = n === 0 ? 'Wähle mind. 1 Slot' : '';
  }
  // Initial state — zeigt direkt „1 Slot fixieren" wenn die Empfehlung greift.
  updateButtons();

  finalizeBtn.addEventListener('click', async () => {
    if (selected.size === 0) return;
    const ok = window.confirm(
      `Diese ${selected.size} Auswahl${selected.size === 1 ? '' : 'en'} als endgültiges Ergebnis fixieren?\n\n` +
      'Die Umfrage wird damit geschlossen — Voter können nicht mehr abstimmen, sehen aber das Ergebnis. ' +
      'Du kannst die Finalisierung jederzeit wieder aufheben.',
    );
    if (!ok) return;
    finalizeBtn.disabled = true;
    finalizeBtn.textContent = 'Wird fixiert …';
    try {
      const message = messageInput.value.trim() || null;
      await updateSurvey(data.admin_token, {
        final_slot_ids: Array.from(selected),
        final_message: message,
      });
      reload();
    } catch (e) {
      window.alert('Fixieren fehlgeschlagen: ' + (e instanceof Error ? e.message : 'unbekannt'));
      finalizeBtn.disabled = false;
      updateButtons();
    }
  });

  return section;
}

function renderSpotlightBanner(vote: SurveyVote, onClose: () => void): HTMLElement {
  const banner = el('div', { class: 'admin-spotlight-banner', role: 'status' });
  banner.append(
    el('span', { class: 'admin-spotlight-banner-icon' }, '👤'),
    el('span', {},
      'Du siehst die Antworten von ',
      el('strong', {}, `„${vote.pseudonym}"`),
      '.',
    ),
  );
  const closeBtn = el('button',
    { type: 'button', class: 'btn-secondary admin-spotlight-back' },
    '↺ Zurück zur Übersicht',
  ) as HTMLButtonElement;
  closeBtn.addEventListener('click', onClose);
  banner.append(closeBtn);
  return banner;
}

function renderHeader(
  title: string,
  data: AdminSurveyResponse,
  voterUrl: string,
  reload: () => void,
): HTMLElement {
  const header = el('section', { class: 'admin-header' });
  const titleRow = el('div', { class: 'admin-title-row' });
  titleRow.append(el('h1', { class: 'admin-title' }, title));
  if (data.location?.trim()) {
    titleRow.append(renderAdminLocation(data.location.trim()));
  }
  const reloadBtn = el(
    'button',
    {
      type: 'button',
      class: 'btn-secondary admin-reload-btn',
      title: 'Daten neu laden',
    },
    '↻ Aktualisieren',
  );
  reloadBtn.addEventListener('click', () => reload());
  titleRow.append(reloadBtn);
  header.append(titleRow);

  const meta = el('div', { class: 'admin-meta' });
  meta.append(
    el('span', { class: 'admin-meta-item' }, `Erstellt: ${formatTimestamp(data.created_at)}`),
    el(
      'span',
      { class: 'admin-meta-item' },
      data.expires_at === null
        ? 'Auto-Löschung: nie'
        : `Auto-Löschung: ${formatTimestamp(data.expires_at)}`,
    ),
    el('span', { class: 'admin-meta-item' },
      `${data.slots.length} Termin${data.slots.length === 1 ? '' : 'e'}`),
    el('span', { class: 'admin-meta-item' },
      `${data.votes.length} Antwort${data.votes.length === 1 ? '' : 'en'}`),
    el('span', { class: 'admin-meta-item' },
      `${data.messages.length} Nachricht${data.messages.length === 1 ? '' : 'en'}`),
  );
  if (data.voting_deadline !== null) {
    const past = data.voting_deadline <= Math.floor(Date.now() / 1000);
    meta.append(el('span',
      { class: 'admin-meta-item admin-meta-deadline ' + (past ? 'is-past' : 'is-future') },
      past
        ? `⏱ Abstimmung beendet (${formatTimestamp(data.voting_deadline)})`
        : `⏱ Deadline: ${formatTimestamp(data.voting_deadline)}`,
    ));
  }
  header.append(meta);

  const linkBox = el('div', { class: 'admin-voter-link' });
  linkBox.append(el('label', { class: 'admin-voter-link-label' }, 'Teilnehmer-Link'));
  const inner = el('div', { class: 'admin-voter-link-inner' });
  const input = el('input', {
    type: 'text',
    class: 'admin-voter-link-input',
    readonly: 'readonly',
    value: voterUrl,
  }) as HTMLInputElement;
  input.addEventListener('focus', () => input.select());

  const copyBtn = el(
    'button',
    { type: 'button', class: 'btn-secondary admin-voter-copy' },
    'Kopieren',
  ) as HTMLButtonElement;
  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(voterUrl);
      copyBtn.textContent = '✓ Kopiert';
      setTimeout(() => (copyBtn.textContent = 'Kopieren'), 1500);
    } catch {
      input.select();
    }
  });

  inner.append(input, copyBtn);

  // Variable wird unten im Block befüllt (embedDetails) und nach dem Inner hinzugefügt.
  // Reichhaltige Share-Message mit Titel, Datum, Uhrzeit, Slot-Anzahl.
  const share = buildVoterShareMessage({
    title: data.title,
    config: data.config,
    slots: data.slots,
    voterUrl,
    location: data.location,
  });

  // Native System-Share (Mac-Share-Sheet, iOS/Android-Native).
  if (typeof navigator.share === 'function') {
    const shareBtn = el(
      'button',
      { type: 'button', class: 'btn-secondary admin-voter-copy admin-voter-share' },
      '↗ Teilen',
    ) as HTMLButtonElement;
    shareBtn.addEventListener('click', async () => {
      try {
        await navigator.share({ title: share.title, text: share.text, url: share.url });
      } catch { /* User-Cancel */ }
    });
    inner.append(shareBtn);
  }
  // OS-unabhängige Direkt-Knöpfe — auch dort verfügbar, wo das System-Sheet
  // nur System-Apps anbietet (z.B. Safari auf macOS ohne WhatsApp-App).
  inner.append(
    el('a', {
      class: 'btn-secondary admin-voter-copy admin-voter-whatsapp',
      href: share.whatsapp,
      target: '_blank',
      rel: 'noopener noreferrer',
    }, '🟢 WhatsApp'),
    el('a', {
      class: 'btn-secondary admin-voter-copy admin-voter-mail',
      href: share.mailto,
    }, '✉️ Mail'),
  );

  // Embed-Code für die Webseite — als zusätzlicher Block unter der Voter-URL.
  const embedDetails = el('details', { class: 'admin-embed-details' });
  embedDetails.append(
    el('summary', { class: 'admin-embed-summary' }, '🌐 Auf eigener Webseite einbetten'),
    el('p', { class: 'admin-embed-hint' },
      'Setze die Umfrage als iframe in deine Vereins-/Firmen-Webseite. ',
      'Voter stimmen direkt im Kontext, ohne zu Slot wechseln.',
    ),
  );
  const embedCode = `<iframe src="${window.location.origin}/embed/vote/${data.voter_token}" width="100%" height="800" style="border:1px solid #ccc;border-radius:8px" title="Slot-Umfrage"></iframe>`;
  const embedTextarea = el('textarea', {
    class: 'admin-embed-code',
    readonly: 'readonly',
    rows: '3',
  }) as HTMLTextAreaElement;
  embedTextarea.value = embedCode;
  embedTextarea.addEventListener('focus', () => embedTextarea.select());
  const embedCopyBtn = el('button',
    { type: 'button', class: 'btn-secondary admin-embed-copy' },
    'Code kopieren',
  ) as HTMLButtonElement;
  embedCopyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      embedCopyBtn.textContent = '✓ Kopiert';
      setTimeout(() => { embedCopyBtn.textContent = 'Code kopieren'; }, 1500);
    } catch {
      embedTextarea.select();
    }
  });
  embedDetails.append(embedTextarea, embedCopyBtn);
  linkBox.append(inner);
  linkBox.append(embedDetails);
  header.append(linkBox);

  // Voter-results visibility selector.
  header.append(renderVisibilitySelector(data));

  return header;
}

function renderVisibilitySelector(data: AdminSurveyResponse): HTMLElement {
  const wrap = el('div', { class: 'admin-visibility' });
  wrap.append(
    el('label', { class: 'admin-visibility-label', for: 'admin-vis-select' },
      'Was sehen Teilnehmer?'),
  );
  const select = el('select', {
    id: 'admin-vis-select',
    class: 'admin-visibility-select',
  }) as HTMLSelectElement;
  const options: Array<{ value: VoterVisibility; label: string }> = [
    { value: 'none',      label: 'Nichts — nur die eigene Stimme' },
    { value: 'aggregate', label: 'Aggregierte Zahlen (Standard) — keine Namen' },
    { value: 'full',      label: 'Voll — inkl. Pseudonyme' },
  ];
  for (const opt of options) {
    select.append(el('option', { value: opt.value }, opt.label));
  }
  select.value = data.voter_results_visibility;

  const status = el('span', { class: 'admin-visibility-status' });
  status.style.display = 'none';

  select.addEventListener('change', async () => {
    const next = select.value as VoterVisibility;
    select.disabled = true;
    status.textContent = 'speichere …';
    status.style.display = '';
    status.classList.remove('is-error');
    try {
      await updateSurvey(data.admin_token, { voter_results_visibility: next });
      data.voter_results_visibility = next;
      status.textContent = '✓ gespeichert';
      setTimeout(() => { status.style.display = 'none'; }, 1500);
    } catch (e) {
      status.textContent = e instanceof Error ? `Fehler: ${e.message}` : 'Fehler beim Speichern';
      status.classList.add('is-error');
      // revert select to last known good
      select.value = data.voter_results_visibility;
    } finally {
      select.disabled = false;
    }
  });

  wrap.append(select, status);
  return wrap;
}

// --- Slot list with voter pills + stars ---

function renderSlotsSection(
  slots: Appointment[],
  votes: SurveyVote[],
  activePseudonym: string | null,
  setSpotlight: (idx: number | null) => void,
): HTMLElement {
  const section = el('section', { class: 'admin-section' });
  section.append(el('h2', { class: 'admin-section-heading' }, 'Termine im Detail'));
  if (slots.length === 0) {
    section.append(el('p', { class: 'admin-empty' }, 'Keine Termine.'));
    return section;
  }

  const list = el('ul', { class: 'admin-slot-list-detailed' });
  for (const slot of sortAppointments(slots)) {
    list.append(renderSlotDetailRow(slot, votes, activePseudonym, setSpotlight));
  }
  section.append(list);
  return section;
}

function renderSlotDetailRow(
  slot: Appointment,
  votes: SurveyVote[],
  activePseudonym: string | null,
  setSpotlight: (idx: number | null) => void,
): HTMLElement {
  const item = el('li', { class: 'admin-slot-detail' });

  const dateLabel = el('div', { class: 'admin-slot-date-block' },
    el('span', { class: 'admin-slot-date' }, formatDateShort(slot.date)),
    el('span', { class: 'admin-slot-time' },
      `${formatTime(slot.startMinutes)}–${formatTime(slot.endMinutes)}`),
  );
  if (slot.lane > 0) {
    dateLabel.append(el('span', { class: 'admin-slot-lane' }, `Spur ${slot.lane + 1}`));
  }
  item.append(dateLabel);

  const stats = computeSlotStats(slot.id, votes);
  const stars = renderStars(stats.starScore);
  const ratingBlock = el('div', { class: 'admin-slot-rating' },
    stars,
    el('span', { class: 'admin-slot-rating-text' },
      stats.total === 0
        ? 'noch keine Stimmen'
        : `${stats.yes}/${votes.length} ja${stats.maybe ? `, ${stats.maybe} ggf.` : ''}${stats.no ? `, ${stats.no} nein` : ''}`,
    ),
  );
  item.append(ratingBlock);

  // Voter pills
  const pillsBlock = el('div', { class: 'admin-slot-pills' });
  const grouped = groupVotesBySlot(slot.id, votes);
  for (const v of grouped.yes) {
    pillsBlock.append(renderVoterPill(v, 'yes', activePseudonym, votes, setSpotlight));
  }
  for (const v of grouped.maybe) {
    pillsBlock.append(renderVoterPill(v, 'maybe', activePseudonym, votes, setSpotlight));
  }
  for (const v of grouped.no) {
    pillsBlock.append(renderVoterPill(v, 'no', activePseudonym, votes, setSpotlight));
  }
  if (grouped.yes.length + grouped.maybe.length + grouped.no.length === 0) {
    pillsBlock.append(el('span', { class: 'admin-slot-no-pills' }, 'noch keine Antworten'));
  }
  item.append(pillsBlock);

  return item;
}

function renderVoterPill(
  vote: SurveyVote,
  choice: 'yes' | 'maybe' | 'no',
  activePseudonym: string | null,
  allVotes: SurveyVote[],
  setSpotlight: (idx: number | null) => void,
  importance?: ImportanceMap,
): HTMLElement {
  const pill = el('button', {
    type: 'button',
    class: `voter-pill is-${choice}`,
    title: `${vote.pseudonym} hat „${labelForChoice(choice)}" gestimmt — klicken für Spotlight`,
  });
  pill.append(
    el('span', { class: 'voter-pill-symbol' }, symbolForChoice(choice)),
    el('span', { class: 'voter-pill-name' }, vote.pseudonym),
  );
  const lvl = importance?.[vote.pseudonym] ?? 0;
  if (lvl > 0) {
    pill.append(el('span', { class: `voter-pill-stars lvl-${lvl}` },
      '★'.repeat(lvl)));
    pill.classList.add(`is-imp-${lvl}`);
  }
  if (vote.pseudonym === activePseudonym) pill.classList.add('is-active');

  pill.addEventListener('click', () => {
    const idx = allVotes.findIndex((v) => v.pseudonym === vote.pseudonym
      && v.created_at === vote.created_at);
    if (idx < 0) return;
    setSpotlight(activePseudonym === vote.pseudonym ? null : idx);
  });
  return pill;
}

/** 3 klickbare Sterne. Klick auf aktuell-aktive Top-Stufe → reset auf 0. */
function renderImportancePicker(
  pseudonym: string,
  state: ViewState,
  onChange: () => void,
): HTMLElement {
  const wrap = el('span', { class: 'star-row', title: 'Wichtigkeit dieses Teilnehmers (lokal, fließt ins Ranking)' });
  const current = state.importance[pseudonym] ?? 0;
  for (let i = 1; i <= 3; i++) {
    const isOn = i <= current;
    const cls = `star-cell${isOn ? ` is-on lvl-${current}` : ''}`;
    const star = el('span', { class: cls, role: 'button', tabindex: '0' }, '★');
    star.addEventListener('click', (e) => {
      e.stopPropagation();
      const next = current === i ? 0 : i;
      if (next === 0) delete state.importance[pseudonym];
      else state.importance[pseudonym] = next;
      saveImportance(state.data.id, state.importance);
      onChange();
    });
    star.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        const next = current === i ? 0 : i;
        if (next === 0) delete state.importance[pseudonym];
        else state.importance[pseudonym] = next;
        saveImportance(state.data.id, state.importance);
        onChange();
      }
    });
    wrap.append(star);
  }
  return wrap;
}

function labelForChoice(c: 'yes' | 'maybe' | 'no'): string {
  return c === 'yes' ? 'kann' : c === 'maybe' ? 'vielleicht' : 'nein';
}

function symbolForChoice(c: 'yes' | 'maybe' | 'no'): string {
  return c === 'yes' ? '✓' : c === 'maybe' ? '?' : '✗';
}

function groupVotesBySlot(
  slotId: string,
  votes: SurveyVote[],
): { yes: SurveyVote[]; maybe: SurveyVote[]; no: SurveyVote[] } {
  const yes: SurveyVote[] = [];
  const maybe: SurveyVote[] = [];
  const no: SurveyVote[] = [];
  for (const vote of votes) {
    const r = vote.responses[slotId];
    if (r === 'yes') yes.push(vote);
    else if (r === 'maybe') maybe.push(vote);
    else if (r === 'no') no.push(vote);
  }
  return { yes, maybe, no };
}

/** Wählt den objektiv besten Slot für die Finalisierung.
 *  Score: ✓-Stimmen * 2 + ?-Stimmen. Bei Gleichstand zählen mehr ✓, dann
 *  früheres Datum, dann frühere Startzeit. null bei 0 Stimmen über alle Slots. */
function recommendBestSlot(
  slots: Appointment[],
  votes: SurveyVote[],
): string | null {
  if (slots.length === 0 || votes.length === 0) return null;
  let bestId: string | null = null;
  let best = { score: -1, yes: -1, date: '￿', start: Number.POSITIVE_INFINITY };
  for (const s of slots) {
    const stats = computeSlotStats(s.id, votes);
    const score = stats.yes * 2 + stats.maybe;
    if (score === 0) continue;
    const candidate = { score, yes: stats.yes, date: s.date, start: s.startMinutes };
    if (
      candidate.score > best.score ||
      (candidate.score === best.score && candidate.yes > best.yes) ||
      (candidate.score === best.score && candidate.yes === best.yes && candidate.date < best.date) ||
      (candidate.score === best.score && candidate.yes === best.yes && candidate.date === best.date && candidate.start < best.start)
    ) {
      best = candidate;
      bestId = s.id;
    }
  }
  return bestId;
}

function computeSlotStats(slotId: string, votes: SurveyVote[]): {
  yes: number; maybe: number; no: number; total: number;
  /** Weighted positive responses (count, not ratio). Use this for ranking. */
  positive: number;
  /** Star rating 0..1 = positive / total voters in the survey. */
  starScore: number | null;
} {
  let yes = 0, maybe = 0, no = 0;
  for (const v of votes) {
    const r = v.responses[slotId];
    if (r === 'yes') yes++;
    else if (r === 'maybe') maybe++;
    else if (r === 'no') no++;
  }
  const total = yes + maybe + no;
  const positive = yes + 0.5 * maybe;
  const totalVoters = votes.length;
  // Stars are out of "all voters in the survey", not just those who answered
  // this slot. Otherwise a slot with one unanimous yes outranks a slot with
  // five out of six yes — wrong intuition.
  const starScore = totalVoters > 0 ? positive / totalVoters : null;
  return { yes, maybe, no, total, positive, starScore };
}

// --- Stars (CSS-gradient based) ---

function renderStars(score: number | null): HTMLElement {
  const wrap = el('div', { class: 'stars' });
  if (score === null) {
    wrap.classList.add('no-rating');
    wrap.append(el('span', { class: 'stars-empty' }, '☆☆☆☆☆'));
    return wrap;
  }
  const pct = Math.max(0, Math.min(100, score * 100));
  const bg = el('span', { class: 'stars-bg' }, '★★★★★');
  const fg = el('span', { class: 'stars-fg' }, '★★★★★');
  fg.style.width = `${pct}%`;
  wrap.append(bg, fg);
  return wrap;
}

// --- Best slots ---

function renderBestSlotsSection(
  state: ViewState,
  activePseudonym: string | null,
  setSpotlight: (idx: number | null) => void,
  rerender: () => void,
): HTMLElement {
  const data = state.data;
  const slots = data.slots;
  const votes = data.votes;
  const section = el('section', { class: 'admin-section admin-best-slots' });

  const headRow = el('div', { class: 'admin-best-head' });
  headRow.append(el('h2', { class: 'admin-section-heading' }, 'Beste Termine'));

  if (votes.length > 0 && slots.length > 0) {
    // View-Toggle (Tabelle ↔ Tier-Karten).
    const toggle = el('div', { class: 'admin-view-toggle', role: 'tablist' });
    for (const [val, label] of [['table', '📊 Tabelle'], ['cards', '🏆 Tier-Karten']] as const) {
      const btn = el('button', {
        type: 'button',
        class: `admin-view-toggle-btn${state.bestSlotsView === val ? ' is-active' : ''}`,
        role: 'tab',
        'aria-selected': state.bestSlotsView === val ? 'true' : 'false',
      }, label) as HTMLButtonElement;
      btn.addEventListener('click', () => {
        if (state.bestSlotsView === val) return;
        state.bestSlotsView = val;
        rerender();
      });
      toggle.append(btn);
    }
    headRow.append(toggle);
  }
  section.append(headRow);

  if (votes.length === 0 || slots.length === 0) {
    section.append(el('p', { class: 'admin-empty' },
      'Sobald Antworten eingehen, erscheint hier das Ranking.'));
    return section;
  }

  // Gewichtetes Ranking (Vielleicht zählt 0.5; Voter-Sterne als Gewicht).
  const ranked = rankSlotsWeighted(slots, votes, state.importance);
  if (ranked.length === 0) {
    section.append(el('p', { class: 'admin-empty' },
      'Keine bewerteten Termine.'));
    return section;
  }

  if (state.bestSlotsView === 'cards') {
    section.append(renderTierCards(ranked, votes, state.importance, activePseudonym, setSpotlight));
  } else {
    section.append(renderBestSlotsTable(ranked, votes, state.importance, activePseudonym, setSpotlight));
  }
  return section;
}

type RankedSlot = {
  slot: Appointment;
  stats: ReturnType<typeof computeSlotStats>;
  /** Gewichteter Yes-Anteil 0..1 (für stacked Bar). */
  yesPct: number;
  maybePct: number;
  /** Gewichteter Score 0..1 = yesPct + maybePct. Tier-Bucket-Key. */
  score: number;
};

function rankSlotsWeighted(
  slots: Appointment[],
  votes: SurveyVote[],
  importance: ImportanceMap,
): RankedSlot[] {
  const totalW = votes.reduce((sum, v) => sum + weightFor(importance, v.pseudonym), 0);
  return slots
    .map((slot) => {
      const stats = computeSlotStats(slot.id, votes);
      let yesW = 0, maybeW = 0;
      for (const v of votes) {
        const w = weightFor(importance, v.pseudonym);
        const r = v.responses[slot.id];
        if (r === 'yes') yesW += w;
        else if (r === 'maybe') maybeW += w * 0.5;
      }
      const yesPct = totalW === 0 ? 0 : yesW / totalW;
      const maybePct = totalW === 0 ? 0 : maybeW / totalW;
      return { slot, stats, yesPct, maybePct, score: yesPct + maybePct };
    })
    .filter((r) => r.stats.total > 0)
    .sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      if (a.stats.no !== b.stats.no) return a.stats.no - b.stats.no;
      if (a.slot.date !== b.slot.date) return a.slot.date < b.slot.date ? -1 : 1;
      return a.slot.startMinutes - b.slot.startMinutes;
    });
}

/** Tier-Bucket-Logik: gleiche Scores → gleicher Tier. Top-3 Tiers bekommen
 *  3/2/1 Kronen — alles dahinter 0. */
function groupByTier(ranked: RankedSlot[]): RankedSlot[][] {
  const tiers: RankedSlot[][] = [];
  let currentKey: number | null = null;
  for (const r of ranked) {
    const key = Math.round(r.score * 1000);
    if (currentKey === null || currentKey !== key) {
      tiers.push([]);
      currentKey = key;
    }
    tiers[tiers.length - 1].push(r);
  }
  return tiers;
}

function crownsForTier(tierIdx: number): number {
  return Math.max(0, 3 - tierIdx);
}

function renderCrowns(count: number): HTMLElement {
  const wrap = el('span', { class: 'crown-row' });
  for (let i = 0; i < count; i++) {
    wrap.append(el('span', { class: `crown-on lvl-${count}` }, '👑'));
  }
  return wrap;
}

/** Klassische Tabellen-Ansicht: pro Slot eine Zeile mit Counts + stacked
 *  Score-Bar (grün = Yes-Anteil, gelb = Vielleicht-Anteil, gewichtet). */
function renderBestSlotsTable(
  ranked: RankedSlot[],
  votes: SurveyVote[],
  importance: ImportanceMap,
  activePseudonym: string | null,
  setSpotlight: (idx: number | null) => void,
): HTMLElement {
  const list = el('ol', { class: 'admin-best-list' });
  const tiers = groupByTier(ranked);
  const slotIdToTier = new Map<string, number>();
  tiers.forEach((tier, idx) => tier.forEach((r) => slotIdToTier.set(r.slot.id, idx)));
  ranked.slice(0, 10).forEach(({ slot, stats, yesPct, maybePct }, idx) => {
    const tierIdx = slotIdToTier.get(slot.id) ?? 99;
    const crowns = crownsForTier(tierIdx);
    const item = el('li', { class: 'admin-best-item' });
    item.append(
      el('span', { class: 'admin-best-rank' }, `#${idx + 1}`),
      crowns > 0
        ? renderCrowns(crowns)
        : el('span', { class: 'admin-best-no-crown' }, ''),
      el('span', { class: 'admin-best-when' },
        `${formatDateShort(slot.date)} · ${formatTime(slot.startMinutes)}–${formatTime(slot.endMinutes)}`,
        slot.lane > 0 ? ` (Spur ${slot.lane + 1})` : '',
      ),
      el('span', { class: 'admin-best-stats' },
        `${stats.yes} ja · ${stats.maybe} vielleicht · ${stats.no} nein`,
      ),
    );

    // Stacked Score-Bar (grün + gelb).
    const bar = el('div', { class: 'score-bar' });
    const yes = el('div', { class: 'score-yes' });
    yes.style.width = `${yesPct * 100}%`;
    const maybe = el('div', { class: 'score-maybe' });
    maybe.style.width = `${maybePct * 100}%`;
    bar.append(yes, maybe);
    item.append(bar);

    const pillRow = el('div', { class: 'admin-slot-pills admin-best-pills' });
    const grouped = groupVotesBySlot(slot.id, votes);
    const sortByImp = (a: SurveyVote, b: SurveyVote) =>
      (importance[b.pseudonym] ?? 0) - (importance[a.pseudonym] ?? 0);
    for (const v of [...grouped.yes].sort(sortByImp)) {
      pillRow.append(renderVoterPill(v, 'yes', activePseudonym, votes, setSpotlight, importance));
    }
    for (const v of [...grouped.maybe].sort(sortByImp)) {
      pillRow.append(renderVoterPill(v, 'maybe', activePseudonym, votes, setSpotlight, importance));
    }
    for (const v of [...grouped.no].sort(sortByImp)) {
      pillRow.append(renderVoterPill(v, 'no', activePseudonym, votes, setSpotlight, importance));
    }
    item.append(pillRow);

    list.append(item);
  });
  return list;
}

/** Tier-Karten-Ansicht: Slots gruppiert nach Score-Tier. Pro Tier eine
 *  farbige Header-Zeile (Lila-Rampe), darunter Karten-Grid. */
function renderTierCards(
  ranked: RankedSlot[],
  votes: SurveyVote[],
  importance: ImportanceMap,
  activePseudonym: string | null,
  setSpotlight: (idx: number | null) => void,
): HTMLElement {
  const wrap = el('div', { class: 'admin-tier-wrap' });
  const tiers = groupByTier(ranked);
  tiers.slice(0, 5).forEach((tier, tierIdx) => {
    const tierClass = tierIdx < 3 ? `is-tier-${tierIdx + 1}` : 'is-tier-other';
    const tierEl = el('div', { class: `admin-tier ${tierClass}` });
    const crownCount = crownsForTier(tierIdx);
    const head = el('div', { class: 'admin-tier-head' });
    head.append(
      el('span', { class: 'admin-tier-rank' },
        tierIdx === 0 ? 'Tier 1 — Beste Termine'
        : tierIdx === 1 ? 'Tier 2'
        : tierIdx === 2 ? 'Tier 3'
        : `Tier ${tierIdx + 1}`),
      crownCount > 0 ? renderCrowns(crownCount) : '',
      el('span', { class: 'admin-tier-score' },
        `Score ${(tier[0].score * 100).toFixed(0)} %`,
      ),
    );
    tierEl.append(head);

    const cardsRow = el('div', { class: 'admin-tier-cards' });
    for (const r of tier) {
      cardsRow.append(renderTierCard(r, votes, importance, activePseudonym, setSpotlight, crownCount));
    }
    tierEl.append(cardsRow);
    wrap.append(tierEl);
  });
  return wrap;
}

function renderTierCard(
  ranked: RankedSlot,
  votes: SurveyVote[],
  importance: ImportanceMap,
  activePseudonym: string | null,
  setSpotlight: (idx: number | null) => void,
  crownCount: number,
): HTMLElement {
  const { slot, stats, yesPct, maybePct } = ranked;
  const card = el('div', { class: 'admin-tier-card' });
  const head = el('div', { class: 'admin-tier-card-head' });
  head.append(
    el('div', { class: 'admin-tier-card-when' },
      el('strong', {}, formatDateShort(slot.date)),
      el('span', { class: 'admin-tier-card-time' },
        ` · ${formatTime(slot.startMinutes)}–${formatTime(slot.endMinutes)}`),
    ),
    crownCount > 0 ? renderCrowns(crownCount) : el('span', {}, ''),
  );
  card.append(head);

  const counts = el('div', { class: 'admin-tier-card-counts' },
    el('span', { class: 'c-yes' },   `${stats.yes} ✓`),
    el('span', { class: 'c-maybe' }, `${stats.maybe} ?`),
    el('span', { class: 'c-no' },    `${stats.no} ✗`),
  );
  card.append(counts);

  const bar = el('div', { class: 'score-bar' });
  const yes = el('div', { class: 'score-yes' });   yes.style.width = `${yesPct * 100}%`;
  const maybe = el('div', { class: 'score-maybe' }); maybe.style.width = `${maybePct * 100}%`;
  bar.append(yes, maybe);
  card.append(bar);

  const grouped = groupVotesBySlot(slot.id, votes);
  const sortByImp = (a: SurveyVote, b: SurveyVote) =>
    (importance[b.pseudonym] ?? 0) - (importance[a.pseudonym] ?? 0);

  const buckets: Array<['yes' | 'maybe' | 'no', SurveyVote[], string]> = [
    ['yes',   [...grouped.yes].sort(sortByImp),   '✓ Ja'],
    ['maybe', [...grouped.maybe].sort(sortByImp), '? Vielleicht'],
    ['no',    [...grouped.no].sort(sortByImp),    '✗ Nein'],
  ];
  for (const [kind, list, label] of buckets) {
    if (list.length === 0) continue;
    const bucket = el('div', { class: 'admin-tier-card-bucket' });
    bucket.append(
      el('div', { class: `admin-tier-card-bucket-label is-${kind}` }, `${label} (${list.length})`),
    );
    const pills = el('div', { class: 'admin-slot-pills' });
    for (const v of list) {
      pills.append(renderVoterPill(v, kind, activePseudonym, votes, setSpotlight, importance));
    }
    bucket.append(pills);
    card.append(bucket);
  }
  return card;
}

// --- Voter list ---

function renderVotersSection(
  data: AdminSurveyResponse,
  state: ViewState,
  onChange: () => void,
): HTMLElement {
  const section = el('section', { class: 'admin-section' });

  const heading = el('h2', { class: 'admin-section-heading' }, 'Teilnehmer');
  if (data.votes.length > 0) {
    heading.append(
      el('span', { class: 'admin-section-hint' },
        ' — Klicke auf einen Eintrag, um nur dessen Antworten zu sehen'),
    );
  }

  // Export-Knöpfe — CSV pro Slot, JSON komplett. Reine Frontend-Generierung,
  // Server liefert die Daten bereits in der Admin-Response.
  const exportRow = el('div', { class: 'admin-export-row' });
  if (data.votes.length > 0) {
    const csvBtn = el('button',
      { type: 'button', class: 'btn-secondary admin-export-btn',
        title: 'Tabellen-Export: pro Slot eine Zeile mit Counts und Pseudonym-Listen' },
      '⬇ CSV',
    ) as HTMLButtonElement;
    csvBtn.addEventListener('click', () => {
      const safeName = (data.title?.trim() || 'slot')
        .replace(/[^A-Za-z0-9äöüÄÖÜß \-_]/g, '').substring(0, 40).trim() || 'slot';
      triggerDownload(`slot-results-${safeName}.csv`, 'text/csv;charset=utf-8',
        buildResultsCsv(data));
    });
    const jsonBtn = el('button',
      { type: 'button', class: 'btn-secondary admin-export-btn',
        title: 'Voller JSON-Export: alle Slots, Stimmen, Aggregate' },
      '⬇ JSON',
    ) as HTMLButtonElement;
    jsonBtn.addEventListener('click', () => {
      const safeName = (data.title?.trim() || 'slot')
        .replace(/[^A-Za-z0-9äöüÄÖÜß \-_]/g, '').substring(0, 40).trim() || 'slot';
      triggerDownload(`slot-results-${safeName}.json`, 'application/json;charset=utf-8',
        JSON.stringify(buildResultsJson(data), null, 2));
    });
    exportRow.append(csvBtn, jsonBtn);
  }
  section.append(heading, exportRow);

  if (data.votes.length === 0) {
    section.append(
      el('p', { class: 'admin-empty' },
        'Noch keine Antworten. Sobald Teilnehmer abstimmen, erscheinen sie hier.'),
    );
    return section;
  }

  // Hint: Stars = Wichtigkeit (lokal). Erklärt UI direkt unter der Header-Zeile.
  section.append(el('p', { class: 'admin-voters-hint' },
    'Klicke auf die ',
    el('span', { class: 'star-hint-token' }, '★★★'),
    ' neben einem Pseudonym, um seine Wichtigkeit zu setzen — die Sterne fließen ',
    'als Gewichtung ins Best-Slot-Ranking ein. ',
    el('em', {}, 'Nur lokal in deinem Browser, der Server sieht nichts davon.'),
  ));

  const list = el('ul', { class: 'admin-voter-list' });
  data.votes.forEach((vote, idx) => {
    const stats = computeVoterStats(vote.responses);
    const isActive = state.spotlightIndex === idx;
    const item = el('li', {
      class: `admin-voter-item${isActive ? ' is-active' : ''} imp-${state.importance[vote.pseudonym] ?? 0}`,
      role: 'button',
      tabindex: '0',
      'aria-pressed': isActive ? 'true' : 'false',
    });
    item.append(
      el('span', { class: 'admin-voter-pseudo' }, vote.pseudonym),
      renderImportancePicker(vote.pseudonym, state, onChange),
      el(
        'span',
        { class: 'admin-voter-stats' },
        `${stats.yes} Ja · ${stats.maybe} Vielleicht · ${stats.no} Nein`,
      ),
      el(
        'span',
        { class: 'admin-voter-time' },
        `aktualisiert ${formatTimestamp(vote.updated_at)}`,
      ),
    );
    const handler = () => {
      state.spotlightIndex = isActive ? null : idx;
      onChange();
    };
    item.addEventListener('click', handler);
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handler();
      }
    });
    list.append(item);
  });
  section.append(list);
  return section;
}

function computeVoterStats(responses: Record<string, 'yes' | 'maybe' | 'no'>): {
  yes: number; maybe: number; no: number;
} {
  let yes = 0, maybe = 0, no = 0;
  for (const v of Object.values(responses)) {
    if (v === 'yes') yes++;
    else if (v === 'maybe') maybe++;
    else if (v === 'no') no++;
  }
  return { yes, maybe, no };
}

// --- Messages section ---

function renderMessagesSection(messages: Message[]): HTMLElement {
  const section = el('section', { class: 'admin-section' });
  section.append(el('h2', { class: 'admin-section-heading' }, 'Nachrichten'));

  if (messages.length === 0) {
    section.append(el('p', { class: 'admin-empty' },
      'Noch keine Nachrichten von Teilnehmern.'));
    return section;
  }

  const list = el('ul', { class: 'admin-message-list' });
  for (const msg of messages) {
    const item = el('li', { class: 'admin-message-item' });
    if (msg.visibility === 'admin') item.classList.add('is-private');

    const header = el('div', { class: 'admin-message-header' });
    header.append(el('span', { class: 'admin-message-pseudo' }, msg.pseudonym));
    header.append(el('span', {
      class: `admin-message-badge ${msg.visibility === 'admin' ? 'is-private' : 'is-public'}`,
    }, msg.visibility === 'admin' ? '🔒 nur Admin' : '📣 öffentlich'));
    header.append(el('span', { class: 'admin-message-time' }, formatTimestamp(msg.created_at)));
    item.append(header);

    item.append(el('p', { class: 'admin-message-text' }, msg.text));
    list.append(item);
  }
  section.append(list);
  return section;
}

function formatTimestamp(unixSeconds: number): string {
  const d = new Date(unixSeconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Trigger-Download via temporäres <a download>. Blob in Memory, sofort
 *  freigegeben — gleichseitig wie die Builder-Exports. */
function triggerDownload(filename: string, mime: string, content: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Pro-Slot-Zeile mit Yes/Maybe/No-Counts + Pseudonym-Listen. Excel-tauglich. */
function buildResultsCsv(data: AdminSurveyResponse): string {
  const sep = ';';
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const rows: string[] = [];
  rows.push([
    'Datum', 'Start', 'Ende',
    'Ja-Anzahl', 'Vielleicht-Anzahl', 'Nein-Anzahl',
    'Ja-Pseudonyme', 'Vielleicht-Pseudonyme', 'Nein-Pseudonyme',
  ].join(sep));
  const sorted = [...data.slots].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    return a.startMinutes - b.startMinutes;
  });
  for (const s of sorted) {
    const grouped = groupVotesBySlot(s.id, data.votes);
    rows.push([
      esc(s.date),
      esc(formatTime(s.startMinutes)),
      esc(formatTime(s.endMinutes)),
      String(grouped.yes.length),
      String(grouped.maybe.length),
      String(grouped.no.length),
      esc(grouped.yes.map((v) => v.pseudonym).join(', ')),
      esc(grouped.maybe.map((v) => v.pseudonym).join(', ')),
      esc(grouped.no.map((v) => v.pseudonym).join(', ')),
    ].join(sep));
  }
  // BOM für Excel-Auto-UTF-8.
  return '﻿' + rows.join('\r\n') + '\r\n';
}

/** Voller JSON-Export — Slots, Stimmen, Aggregate. Datenmodell-stabil. */
function buildResultsJson(data: AdminSurveyResponse): Record<string, unknown> {
  const sorted = [...data.slots].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    return a.startMinutes - b.startMinutes;
  });
  return {
    survey: {
      id: data.id,
      title: data.title,
      location: data.location,
      created_at: data.created_at,
      expires_at: data.expires_at,
      voting_deadline: data.voting_deadline,
      voter_count: data.votes.length,
      finalized: (data.final_slot_ids?.length ?? 0) > 0,
      final_slot_ids: data.final_slot_ids,
      final_message: data.final_message,
    },
    slots: sorted.map((s) => {
      const grouped = groupVotesBySlot(s.id, data.votes);
      return {
        id: s.id,
        date: s.date,
        start: formatTime(s.startMinutes),
        end: formatTime(s.endMinutes),
        yes: grouped.yes.length,
        maybe: grouped.maybe.length,
        no: grouped.no.length,
        yes_pseudonyms: grouped.yes.map((v) => v.pseudonym),
        maybe_pseudonyms: grouped.maybe.map((v) => v.pseudonym),
        no_pseudonyms: grouped.no.map((v) => v.pseudonym),
      };
    }),
    votes: data.votes.map((v) => ({
      pseudonym: v.pseudonym,
      responses: v.responses,
      created_at: v.created_at,
      updated_at: v.updated_at,
    })),
  };
}

/** Survey-Location als 📍-Pille. Klickbar als Link, wenn http(s)://. */
function renderAdminLocation(location: string): HTMLElement {
  const isUrl = /^https?:\/\//i.test(location);
  const wrap = el('span', { class: 'admin-location' }, '📍 ');
  if (isUrl) {
    wrap.append(el('a', {
      href: location,
      target: '_blank',
      rel: 'noopener noreferrer',
      class: 'admin-location-link',
    }, location));
  } else {
    wrap.append(location);
  }
  return wrap;
}
